import { useEffect } from 'react'
import type { JsonLd } from '../lib/schema-org'
import { absoluteUrl } from '../lib/site-origin'
import { useSchemaOrg } from './useSchemaOrg'

const BASE = 'BNKR'

export type PageMeta = {
  title: string
  description?: string | null
  /** Path or absolute URL */
  canonical?: string | null
  ogImage?: string | null
  ogType?: string
  noindex?: boolean
  /** Schema.org JSON-LD @graph (or single node with @context). */
  jsonLd?: JsonLd | null
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string | null) {
  const sel = `meta[${attr}="${key}"]`
  let el = document.querySelector(sel) as HTMLMetaElement | null
  if (!content) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

function upsertCanonical(href: string | null) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!href) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement('link')
    el.rel = 'canonical'
    document.head.appendChild(el)
  }
  el.href = href
}

/** Title, description, canonical, Open Graph, Twitter, and optional JSON-LD. */
export function usePageMeta(meta: PageMeta | null) {
  useSchemaOrg(meta?.jsonLd ?? null)

  useEffect(() => {
    if (!meta) return

    const title = `${meta.title} · ${BASE}`
    document.title = title

    const desc = meta.description?.trim() || null
    upsertMeta('name', 'description', desc)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', desc)
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', desc)
    upsertMeta('property', 'og:type', meta.ogType ?? 'website')

    const canonical = meta.canonical ? absoluteUrl(meta.canonical) : null
    upsertCanonical(canonical)
    upsertMeta('property', 'og:url', canonical)

    const image = meta.ogImage ? absoluteUrl(meta.ogImage) : null
    upsertMeta('property', 'og:image', image)
    upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary')
    upsertMeta('name', 'twitter:image', image)

    const robots = meta.noindex ? 'noindex, nofollow' : 'index, follow'
    upsertMeta('name', 'robots', robots)

    return () => {
      document.title = BASE
    }
  }, [
    meta?.title,
    meta?.description,
    meta?.canonical,
    meta?.ogImage,
    meta?.ogType,
    meta?.noindex,
    meta?.jsonLd,
  ])
}
